import { TItemsContainer } from "types/items-container";
import { TTargetType } from "types/target-type";
import { dollarFormatter } from "utils";
import EachProduct from "./eachProduct";
import Taxes from "./components/tax/taxes";
import {
	calculateGrandTotal,
	calculateTaxTotal,
	calculateTotal,
} from "./utils";
import * as React from "react";
import { useTranslation } from 'react-i18next';
import { useLineItemContext } from "./context/LineItemContext";
import TooltippedElement from "components/TooltippedElement";
import { FaInfoCircle } from "react-icons/fa";

export default function ReadLayout(props: {
	data?: TItemsContainer;
	id: string;
	type: TTargetType;
	view?: "read" | "edit-taxes";
}) {
	const { t } = useTranslation();
	const tBase = "containers.lineItems.readLayout";
	const tr = (key: string) => t(`${tBase}.${key}`);

	const { showMath, taxableProductTotal, taxTotal, taxes, noTax } = useLineItemContext();

	const priceType = props.data?.price_type || "list";
	const rateLabel =
		priceType === "list"
			? "list_price"
			: priceType === "sale"
			? "unit_price"
			: "install_price";

	let products =
		props.data?.products &&
		props.data?.products.length > 0 &&
		props.data.products;

	if (products && props.data?.hide_empty) {
		products = products.filter((product) => product.quantity);
	}

	const populateProducts =
		products &&
		products.map((product, i, f) => {
			if (!product) return null;

			const showHeader =
				i === 0 ||
				product.fk_ingroup_id !== f[i - 1].fk_ingroup_id ||
				(!product.fk_ingroup_id &&
					product.root_id !== f[i - 1].root_id);

			const group =
				showHeader && product.fk_ingroup_id && product.group_name;

			const cat = showHeader && (product.root_name || tr("Service"));

			return (
				<React.Fragment key={product.id}>
					{(i === 0 ||
						f[i - 1].fk_estimate_option_id !==
							product.fk_estimate_option_id) && (
						<span className="bg-[#ff4f00]/10 text-[#ff4f00] p-1 text-sm rounded">
							{product.estimate_option_label ||
								tr("Additional Charges")}
						</span>
					)}
					{(group || cat) && (
						<h3 className="mt-4 text-sm font-bold uppercase text-slate-500">
							{product.group_name || cat}
						</h3>
					)}
					<EachProduct
						priceType={priceType}
						rateLabel={rateLabel}
						hideEmpty={props.data?.hide_empty || false}
						{...product}
					/>
				</React.Fragment>
			);
		});
		
	const total = calculateTotal(props.data?.products);
	const afterTaxTotal = calculateTaxTotal(props.data?.products || [], taxes);
	const grandTotal = calculateGrandTotal(total, afterTaxTotal);


	const taxableSubtotal = (props.data?.products ?? [])
		.filter((product) => product.taxable)
		.reduce(
			(prev: any, product) => ({
				list: prev.list + product.quantity * +(product.list_price || 0),
				sale: prev.sale + product.quantity * +(product.unit_price || 0),
				install: prev.install + product.quantity * +(product.install_price || 0),
			}),
			{ list: 0, sale: 0, install: 0 }
		);

	const array: ("list" | "install" | "sale")[] = ["list", "sale", "install"];

	const restOfPriceType = array
		.filter((f) => f !== priceType)
		.reduce(
			(prev, f, i, all) =>
				prev +
				`${tr(f.toLocaleUpperCase())}: ${dollarFormatter(grandTotal[f])} ${
					i !== all.length - 1 ? " | " : ""
				}`,
			""
		);

	return (
		<div style={{ display: "flex", flexDirection: "column" }}>
			<h2
				style={{
					textTransform: "capitalize",
					alignSelf: "flex-end",
				}}
			>
				{/* {props.data?.price_type && tr(props.data?.price_type)} {tr("Price")} */}
				{props.data?.price_type && tr(props.data?.price_type + " Price")}
			</h2>
			{populateProducts}
			<div className="flex justify-end mt-2" id="taxes-container">
			{!noTax && (
				<Taxes
					view={props.view || "read"}
					total={total}
					taxableTotal={taxableSubtotal}
					id={props.id}
					type={props.type}
					priceType={priceType}
					/>
				)}
			</div>
			<div className="flex justify-end">
				<div className="w-[500px] flex justify-between">
					{showMath ? (
						<div className="text-[1.5em]  flex items-end">
							<div className="relative">
								<div className="text-green-600 mr-1">{dollarFormatter(taxableProductTotal)}</div>
								<div className="absolute top-[-2px] right-[-4px] text-xs">
									<TooltippedElement
										message={"Total of each product (taxable) after fees and discounts applied"}
										placement="top"
										element={<FaInfoCircle className="text-blue-500" size={12} />}
									/>
								</div>
							</div>
							<div className="text-gray-500 mx-2">+</div>
							<div className="relative">
								<div className="text-gray-500">{dollarFormatter(taxTotal)}</div>
								<div className="absolute top-[-2px] right-[-8px] text-xs">
									<TooltippedElement
										message={"Total tax"}
										placement="top"
										element={<FaInfoCircle className="text-blue-500" size={12} />}
									/>
								</div>
							</div>
							<div className="text-gray-500 ml-3">=</div>
						</div>
					) : (
						<div />
					)}
					<div className="">
						<div className="text-[0.8em]">
							{restOfPriceType}
						</div>
						<div className="text-[1.5em] text-right capitalize relative">
							{props.data?.price_type} Total: {dollarFormatter(grandTotal[priceType])}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
